/* eslint-disable @typescript-eslint/no-explicit-any */
import { CHART_RESOLUTIONS } from "../../constants/chart/resolutions";
import { ChartBar } from "../../hooks/api/candles/useCandles";

/**
 * A TradingView Charting Library datafeed that serves THIS exchange's own
 * fills and nothing else.
 *
 * History comes from `GET /candles` (the indexer's OHLCV buckets, built from
 * our OrderMatched events); live bars from the `candles` websocket channel
 * (pushed in by the React component via `pushLiveBar`) and, because a socket
 * can be silent — on all three testnets /socket.io is served by a relay that
 * never answers the candles channel — from a short poll of the newest bars.
 * No bar is ever forward-filled (`has_empty_bars: false`): a period with no
 * trades has no candle, exactly as on the native chart.
 *
 * Kept free of React and of the library's globals so it can be unit tested.
 */

/** Our canonical resolutions ARE TradingView resolution strings. */
export const TV_RESOLUTIONS = CHART_RESOLUTIONS.map((r) => r.id);

export interface OwnMarketInfo {
  /** Instrument name, e.g. "BTC-PERP". Also the TradingView symbol. */
  name: string;
  /** Label shown in the chart legend, e.g. "BTC-USD". */
  displayName: string;
  priceDecimals: number;
  amountDecimals: number;
  /** Venue label (this exchange's brand). */
  exchange?: string;
}

export interface TvBar {
  time: number; // ms
  open: number;
  high: number;
  low: number;
  close: number;
  volume: number;
}

export interface OwnDatafeedDeps {
  getMarket: (symbol: string) => OwnMarketInfo | undefined;
  fetchBars: (p: { instrumentName: string; resolution: string; to?: number; countback?: number }) => Promise<ChartBar[]>;
  /** Poll for the newest bars while subscribed. 0 disables the poll. */
  pollMs?: number;
  /** Receives every bar handed to the library, for the QA handle. */
  onDelivered?: (key: string, bars: TvBar[]) => void;
  /** First load of a series: "empty" (no fills), "loaded", or "error". */
  onSeriesStatus?: (key: string, status: "empty" | "loaded" | "error") => void;
}

interface Subscription {
  uid: string;
  symbol: string;
  resolution: string;
  onTick: (bar: TvBar) => void;
  lastTime: number; // ms of the newest bar delivered
  timer?: ReturnType<typeof setInterval>;
}

export const toTvBar = (b: ChartBar): TvBar => ({
  time: b.time * 1000,
  open: b.open,
  high: b.high,
  low: b.low,
  close: b.close,
  volume: b.volume,
});

export const seriesKey = (symbol: string, resolution: string) => `${symbol}@${resolution}`;

export const createOwnDatafeed = (deps: OwnDatafeedDeps) => {
  const subs = new Map<string, Subscription>();
  // Newest bar each series' history ended on, so a subscription never hands
  // the library a bar older than the chart's last one.
  const historyEnd = new Map<string, number>();
  const pollMs = deps.pollMs ?? 15_000;

  /** Hand a bar to every subscriber of its series, newest-or-equal only. */
  const deliver = (symbol: string, resolution: string, bar: TvBar) => {
    let drawn = false;
    subs.forEach((s) => {
      if (s.symbol !== symbol || s.resolution !== resolution) return;
      // The library rejects a bar older than the one it last got.
      if (bar.time < s.lastTime) return;
      s.lastTime = bar.time;
      // A copy: the library rewrites bar objects in place (DWM alignment).
      s.onTick({ ...bar });
      drawn = true;
    });
    const key = seriesKey(symbol, resolution);
    deps.onDelivered?.(key, [bar]);
    // The first trade on an empty market: clear "No trades yet".
    if (drawn) deps.onSeriesStatus?.(key, "loaded");
  };

  const poll = async (s: Subscription) => {
    try {
      const bars = await deps.fetchBars({
        instrumentName: s.symbol,
        resolution: s.resolution,
        to: Math.floor(Date.now() / 1000) + 1,
        countback: 2,
      });
      bars.map(toTvBar).forEach((b) => deliver(s.symbol, s.resolution, b));
    } catch {
      /* transient; the next poll retries */
    }
  };

  return {
    onReady: (cb: (config: any) => void) => {
      setTimeout(() =>
        cb({
          supported_resolutions: TV_RESOLUTIONS,
          supports_marks: false,
          supports_timescale_marks: false,
          supports_time: true,
          exchanges: [],
          symbols_types: [],
        })
      );
    },

    searchSymbols: (_input: string, _exchange: string, _type: string, cb: (r: any[]) => void) => cb([]),

    resolveSymbol: (symbol: string, onResolved: (info: any) => void, onError: (reason: string) => void) => {
      const market = deps.getMarket(symbol);
      if (!market) {
        setTimeout(() => onError(`Unknown market "${symbol}"`));
        return;
      }
      setTimeout(() =>
        onResolved({
          name: market.displayName,
          full_name: market.name,
          description: market.displayName,
          ticker: market.name,
          type: "crypto",
          session: "24x7",
          // Shown in the library's series tooltip; empty rendered "undefined".
          exchange: market.exchange ?? "",
          listed_exchange: market.exchange ?? "",
          timezone: "Etc/UTC",
          format: "price",
          pricescale: 10 ** market.priceDecimals,
          minmov: 1,
          has_intraday: true,
          has_daily: true,
          has_weekly_and_monthly: true,
          // Never invent candles for periods with no trades.
          has_empty_bars: false,
          supported_resolutions: TV_RESOLUTIONS,
          intraday_multipliers: ["1", "5", "15", "30", "60", "240"],
          volume_precision: market.amountDecimals,
          data_status: "streaming",
        })
      );
    },

    getBars: (
      symbolInfo: any,
      resolution: string,
      periodParams: { from: number; to: number; countBack?: number; firstDataRequest?: boolean },
      onHistory: (bars: TvBar[], meta: { noData: boolean }) => void,
      onError: (reason: string) => void
    ) => {
      const symbol = symbolInfo.ticker || symbolInfo.full_name;
      deps
        .fetchBars({
          instrumentName: symbol,
          resolution,
          to: periodParams.to,
          countback: Math.max(periodParams.countBack ?? 0, 300),
        })
        .then((bars) => {
          // Strictly before `to`: the next page's `to` is this page's oldest
          // bar, and a duplicate there is a "time order violation" in the lib.
          const page = bars.filter((b) => b.time < periodParams.to).map(toTvBar);
          const key = seriesKey(symbol, resolution);
          if (page.length) {
            const newest = page[page.length - 1].time;
            historyEnd.set(key, Math.max(historyEnd.get(key) ?? 0, newest));
            deps.onDelivered?.(key, page);
          }
          if (periodParams.firstDataRequest) {
            deps.onSeriesStatus?.(key, page.length ? "loaded" : "empty");
          }
          // Copies: the library rewrites bar objects in place (DWM alignment),
          // which must not reach our cache or the QA handle.
          onHistory(
            page.map((b) => ({ ...b })),
            { noData: page.length === 0 }
          );
        })
        .catch((e) => {
          if (periodParams.firstDataRequest) deps.onSeriesStatus?.(seriesKey(symbol, resolution), "error");
          onError(String(e?.message ?? e));
        });
    },

    subscribeBars: (symbolInfo: any, resolution: string, onTick: (bar: TvBar) => void, uid: string) => {
      const symbol = symbolInfo.ticker || symbolInfo.full_name;
      const s: Subscription = {
        uid,
        symbol,
        resolution,
        onTick,
        lastTime: historyEnd.get(seriesKey(symbol, resolution)) ?? 0,
      };
      if (pollMs > 0) s.timer = setInterval(() => void poll(s), pollMs);
      subs.set(uid, s);
    },

    unsubscribeBars: (uid: string) => {
      const s = subs.get(uid);
      if (s?.timer) clearInterval(s.timer);
      subs.delete(uid);
    },

    /** A live bar from the `candles` websocket channel. */
    pushLiveBar: (symbol: string, resolution: string, bar: ChartBar) =>
      deliver(symbol, resolution, toTvBar(bar)),

    /** Stop every poll (component unmount). */
    dispose: () => {
      subs.forEach((s) => s.timer && clearInterval(s.timer));
      subs.clear();
    },
  };
};

export type OwnDatafeed = ReturnType<typeof createOwnDatafeed>;
